import axios from 'axios'

import router from '@/router'
import { removeDuplicateCookie } from '@/model/MaCookieStorage'
import { maSessionStorage } from '@/lib/storage'
import { useMaCore } from '@/composables/maCore'
import { DAY_IN_MS, randomString, SEC_IN_MS } from '@/base/MaUtils'
import { maLogger } from '@/base/logger'
import { DEBUG_SESSION_EXPIRED, GATEWAY_DOMAIN, version } from '@/base/constants/ConfigVars'
import { getCookieAuth, getCurrentAccessToken, logout, setRefreshedAcccessToken } from '@/api/utils/session'
import * as MaRequestCache from './MaRequestCache'

import { MaNotification } from '@mobileaction/action-kit'

/**
 * Axios instance for handling API requests with global settings and interceptors.
 */
const REQUESTER = axios.create({
  baseURL: GATEWAY_DOMAIN,
  responseType: 'json',
  withCredentials: false,
  headers: { 'Content-Type': 'application/json; charset=utf-8' },
  transformResponse: [
    ...axios.defaults.transformResponse,
    convertDataToNorm, // Make sure all data is normalized
  ],
})

/**
 * Default options used for request pooling and caching.
 */
const DEFAULT_OPTIONS = {
  cacheEnabled: false,
  clearCached: false,
  cacheDelay: 10,
  rawResponse: false,
  cacheTimeout: DAY_IN_MS,
}

const pools = {}
let pendingRequests = []
let isTokenRefreshInProgress = false
let refreshTokenPromise = null
// Maximum time to wait for token refresh
const TOKEN_REFRESH_TIMEOUT = 15 * 1000 // 15 seconds

// converts data object to the norm object type defined in /dashboard-ui/wiki
function convertDataToNorm(data) {
  if (!data) {
    return {
      success: false,
      errors: ['NO_DATA'],
      data,
      errorData: {},
    }
  }

  // Return directly if it is in correct format
  const hasSuccessKey = Object.hasOwnProperty.call(data, 'success')
  if (hasSuccessKey && (data.success === true || Object.hasOwnProperty.call(data, 'errors'))) {
    if (data.errorMessage) {
      if (data.errorData) { // might overwrite existing err msg
        data.errorData.errorMessage = data.errorMessage
      }
      else {
        data.errorData = { errorMessage: data.errorMessage }
      }
    }
    return data
  }

  const success = hasSuccessKey
    ? data.success
    : data.status !== 'fail'
  return {
    success,
    errors: data.message || [],
    data: success && data,
    errorData: !success && data,
  }
}

/**
 * Displays session expired notification and clears analytics state if needed.
 *
 * @param {boolean} [multipleLoginDetected] - Indicates if the session was invalidated due to another login.
 */
function showSessionExpiredWarning(multipleLoginDetected = false) {
  const multipleLoginDetectedMessage = 'Login activity detected from another browser, you have been redirected to the login page'
  const sessionExpiredMessage = 'Your session has expired, you have been redirected to the login page'

  if (Date.now() - maSessionStorage.lastSessionExpiredShown > 5 * SEC_IN_MS) {
    maSessionStorage.lastSessionExpiredShown = Date.now()
    MaNotification.warning({
      title: 'Session Expired',
      description: multipleLoginDetected ? multipleLoginDetectedMessage : sessionExpiredMessage,
      size: 'large',
    })
  }

  if (DEBUG_SESSION_EXPIRED) {
    if (multipleLoginDetected) {
      maLogger.warn(multipleLoginDetectedMessage, (new Error('Multiple Login Detected!')).stack)
    }
    else {
      maLogger.warn(sessionExpiredMessage, (new Error('Session Expired!')).stack)
    }

    const { analytics } = useMaCore()
    analytics?.resetUser()
    analytics?.clearIntercomUserInfo()
  }
}

/**
 * Handles expired token scenarios by showing warnings and redirecting to login.
 *
 * @param {boolean} [multipleLoginDetected]
 */
function tokenExpired(multipleLoginDetected = false) {
  const route = router.resolve(window.location.pathname)
  if (route.meta.disableAuthCheck)
    return

  if (Date.now() - maSessionStorage.lastSessionExpiredShown < 1000) {
    logout()
    maSessionStorage.lastSessionExpiredShown = Date.now()
    return
  }

  showSessionExpiredWarning(multipleLoginDetected)
  logout()
}

/**
 * Updates JWT and refresh token from response headers and stores them.
 *
 * @param {object} data - Response data.
 * @param {object} headers - Response headers containing token info.
 * @returns {object} - Returns original response data.
 */
function storeNewJwt(data, headers) {
  const _jwt = headers['ma-jwt']
  if (_jwt) {
    setRefreshedAcccessToken(_jwt)
    isTokenRefreshInProgress = false
    onTokenRefreshComplete()
  }
  return data
}

/**
 * Executes all queued requests waiting for token refresh.
 */
function onTokenRefreshComplete() {
  const requestsToProcess = [...pendingRequests]
  pendingRequests = []
  requestsToProcess.forEach(callback => callback())
}

/**
 * Attempts to refresh tokens using the original request configuration.
 *
 * @param {object} originalRequest - Axios request config that triggered the refresh.
 * @returns {Promise<void>}
 */
async function requestWithRefreshToken(originalRequest) {
  if (refreshTokenPromise) {
    try {
      await refreshTokenPromise
      return // Normal flow should continue
    }
    catch (_) {
      // Previous refresh attempt failed, we should NOT try again
      refreshTokenPromise = null
      tokenExpired()
      throw new Error('Token refresh failed previously')
    }
  }

  try {
    isTokenRefreshInProgress = true
    const userAuth = getCookieAuth()
    if (!userAuth?.refreshToken) {
      throw new Error('No refresh token available')
    }

    const refreshRequestConfig = {
      ...originalRequest,
      headers: {
        ...originalRequest.headers,
        Authorization: `Bearer ${userAuth.refreshToken}`,
      },
      _isRefreshAttempt: true,
      // Prevent refresh token requests from being cancelled
      cancelToken: undefined,
    }

    // Use a direct axios request without cancelToken to prevent cancellation
    refreshTokenPromise = REQUESTER.request(refreshRequestConfig)
    await refreshTokenPromise
    return refreshTokenPromise
  }
  catch (error) {
    maLogger.error('Failed to refresh token:', error)
    throw error // Propagate the error
  }
  finally {
    isTokenRefreshInProgress = false
    refreshTokenPromise = null
    onTokenRefreshComplete()
  }
}

// Interceptor to handle request modifications for authentication
REQUESTER.interceptors.request.use((config) => {
  const authHeader = config.headers?.Authorization

  if (authHeader && authHeader.startsWith('Bearer ')) {
    if (isTokenRefreshInProgress && !config._isRefreshAttempt) {
      return new Promise((resolve, reject) => {
        // Add a timeout to prevent requests from hanging indefinitely
        const timeoutId = setTimeout(() => {
          reject(new Error('Token refresh timeout'))
        }, TOKEN_REFRESH_TIMEOUT)

        pendingRequests.push(() => {
          clearTimeout(timeoutId)
          const accessToken = getCurrentAccessToken()
          if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`
          }
          resolve(config)
        })
      })
    }
  }

  return config
}, (error) => {
  return Promise.reject(error)
})

// Single response interceptor to handle both success and error cases
REQUESTER.interceptors.response.use(
  (response) => {
    storeNewJwt(response.data, response.headers)
    return response
  },
  async (error) => {
    // TODO: It can be removed after June 6, 2025.
    removeDuplicateCookie()

    const originalRequest = error.config

    // Prevent infinite loops with refresh token requests
    if (originalRequest?._isRefreshAttempt) {
      tokenExpired()
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true

      try {
        await requestWithRefreshToken(originalRequest)
        const accessToken = getCurrentAccessToken()

        if (!accessToken) {
          throw new Error('Failed to get access token after refresh')
        }

        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        return REQUESTER(originalRequest)
      }
      catch (refreshError) {
        maLogger.error('Token refresh failed:', refreshError)
        tokenExpired(error.response?.data?.errors?.includes('AUTH_MULTIPLE_LOGIN'))
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  },
)

/**
 * Cancels all ongoing requests in a given pool and removes the pool.
 *
 * @param {string} poolId - Pool identifier.
 */
function clearPool(poolId) {
  if (!poolId) {
    maLogger.warn('Invalid pool id given:', poolId)
    return
  }

  if (!(poolId in pools)) {
    maLogger.warn('Tried to clean non-existing pool with id:', poolId)
    return
  }

  const p = pools[poolId]
  Object.keys(p).forEach((reqId) => {
    p[reqId].cancel()
  })

  delete pools[poolId]
}

/**
 * Warns if caching is used on non-GET requests
 *
 * @param {string} url - The request URL
 * @param {object} axiosCfg - Axios configuration
 */
function notifyIfNonGetCache(url, axiosCfg) {
  if (!axiosCfg) {
    return
  }

  if (axiosCfg.method !== 'GET') {
    maLogger.warn('Using cache on a non-GET request, this can cause problems. See notifyIfNonGetCache', url)
    /**
     * First post methods have body which we ignore and mostly post requests are for updating not data fetching.
     * Second we only use url as a key so using if there are two endpoints with same url structure but different
     * methods they will overwrite each other.
     */
  }
}

/**
 * Sends a request with optional caching, authentication, and pooling support.
 *
 * @param {string} url - The endpoint to call.
 * @param {object} [axiosCfg] - Axios configuration options.
 * @param {object} [poolerOpts] - Additional options such as caching and request pooling.
 * @returns {Promise<any>} - Returns a promise that resolves with response data or rejects with error.
 */
export function makeRequest(url, axiosCfg = {}, poolerOpts = {}) {
  // load axios config with defaults
  const _axiosCfg = Object.assign({ method: 'GET' }, axiosCfg)

  // load pooler options with defaults
  const _poolerOpts = Object.assign(
    {
      id: window.location.href,
      disableAuthCheck: false,
    },
    DEFAULT_OPTIONS,
    poolerOpts,
  )

  maLogger.info(`Making a request cached=${_poolerOpts.cacheEnabled} ${_axiosCfg.method} ${url}`)

  // clear cache if specified
  if (_poolerOpts.clearCached) {
    removeCached(url)
  }

  // generate a random request id
  const reqId = randomString(16)
  const cancelSource = axios.CancelToken.source()

  // will return a promise I promise :)
  const p = new Promise((resolve, reject) => {
    // Check for cached response if caching is enabled
    if (_poolerOpts.cacheEnabled) {
      notifyIfNonGetCache(url, _axiosCfg)
      const cachedData = MaRequestCache.get(url)
      if (cachedData) {
        setTimeout(() => resolve(cachedData), _poolerOpts.cacheDelay)
        return
      }
    }

    // Add request to the pool
    pools[_poolerOpts.id] = pools[_poolerOpts.id] || {}
    pools[_poolerOpts.id][reqId] = {
      id: reqId,
      cancel: cancelSource.cancel,
      ts: Date.now(),
    }

    // Set up request headers
    const headers = Object.assign({}, _axiosCfg.headers)

    // Handle authentication if required
    if (!_poolerOpts.disableAuthCheck) {
      const userAuth = getCookieAuth()
      if (!userAuth) {
        tokenExpired()
        return
      }

      headers.Authorization = `Bearer ${userAuth.jwt || userAuth.refreshToken}`
    }

    headers['MA-App-Version'] = version
    _axiosCfg.headers = headers

    // Prepare final request configuration
    const requestConfig = Object.assign({}, _axiosCfg, {
      url,
      cancelToken: cancelSource.token,
    })

    // Make the request
    REQUESTER.request(requestConfig)
      .then(({ data }) => {
        if (data.success) {
          // Cache the response if caching is enabled
          if (_poolerOpts.cacheEnabled) {
            notifyIfNonGetCache(url, _axiosCfg)
            MaRequestCache.set(url, data.data, _poolerOpts.cacheTimeout)
          }

          resolve(data.data)
        }
        else {
          // Handle API errors
          let errors = data.errors || ['FETCH_ERROR', 'MALFORMED_RESPONSE']

          if (typeof errors === 'string') {
            errors = [errors]
          }

          if (!_poolerOpts.disableAuthCheck && errors.find(e => e === 'NOT_LOGGED_IN')) {
            clear()
            showSessionExpiredWarning()
          }

          maLogger.warn(`Request failed with known code ${_axiosCfg.method} ${url} with:`, errors, data)
          data.errors = errors
          data.errorData = data.errorData || {}

          reject(data)
        }
      })
      .catch((error) => {
        // Handle request cancellation
        const { response, message } = error

        if (['ABORT', 'canceled'].includes(message))
          return reject(new Error('ABORT'))

        // Get error status and data
        const status = response ? response.status : message

        const data = response?.data ?? {}

        if (_poolerOpts.rawResponse === true) {
          data.raw = response
        }

        data.errors = Array.isArray(data.errors) ? data.errors : ['FETCH_ERROR', data.message || 'MALFORMED_FAIL_RESPONSE']
        data.errorData = data.errorData ?? {}

        if (status === 401) {
          if (data.errors?.includes('AUTHENTICATION_FAILED')) {
            if (_poolerOpts.disableAuthCheck !== true) {
              tokenExpired(data.errors?.includes('AUTH_MULTIPLE_LOGIN'))
            }
          }
          reject(data)
          return
        }

        maLogger.warn(`Request failed ${_axiosCfg.method} ${url} with:`, error)

        reject(data, status)
      })
      .finally(() => {
        // Clean up the request from the pool
        if (pools[_poolerOpts.id] && pools[_poolerOpts.id][reqId]) {
          delete pools[_poolerOpts.id][reqId]
        }
      })
  })

  // set pool, request id, and cancel function for future cancel/clears
  p.poolId = _poolerOpts.id
  p.reqId = reqId
  p.cancel = () => cancelSource.cancel('ABORT')

  return p
}

/**
 * A shortcut for making a request with caching enabled.
 *
 * @param {string} url
 * @param {object} [ajaxOptions]
 * @param {object} [poolerOptions]
 * @returns {Promise<any>}
 */
export function cacheRequest(url, ajaxOptions = {}, poolerOptions = {}) {
  const _poolerOptions = Object.assign({}, poolerOptions, { cacheEnabled: true })
  return makeRequest(url, ajaxOptions, _poolerOptions)
}

/**
 * Removes the cached response for a specific URL.
 *
 * @param {string} url - URL whose cache should be deleted.
 */
export function removeCached(url) {
  MaRequestCache.del(url)
}

/**
 * Clears request pool(s) and sessionStorage.
 *
 * @param {string} [id] - If provided, only clears a specific request pool.
 */
export function clear(id) {
  sessionStorage.clear()

  // if id is given just destroy given id
  if (id && id in pools) {
    clearPool(id)
    return
  }

  // check all pools of all ids
  Object.keys(pools).forEach((poolId) => {
    clearPool(poolId)
  })
}

/**
 * Sends a raw pre-flight GET request using the base gateway URL.
 *
 * @param {string} url - Endpoint path to call.
 * @returns {Promise<any>}
 */
export function makePreRequest(url) {
  return axios.get(GATEWAY_DOMAIN + url)
}

// Export the configured Axios instance
export { REQUESTER }
